import { blessnetMainnet, blessnetSepolia } from "@/config/bless-chain";
import { useModularPermissionsStore } from "@/features/mod-perms/stores/modular-permissions.store";
import { ChainMode } from "@/types/modular-permissions.types";
import { useEffect } from "react";
import { useAccount, useSwitchChain } from "wagmi";

/**
 * A custom hook that automatically switches the user's network to the Bless Network
 * when they are connected to a different chain.
 *
 * @remarks
 * This hook monitors the user's connection status and current chain ID.
 * If the user is connected but not on the Bless Network, it automatically
 * triggers a chain switch request.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   useSwitchToBless();
 *   // ... rest of your component
 * }
 * ```
 *
 * @returns void
 */

//? Chains to allow for use
const useSwitchToBless = () => {
  const { isConnected, chain } = useAccount();

  const setActiveChainMode = useModularPermissionsStore(
    (state) => state.setActiveChainMode,
  );

  useEffect(() => {
    if (chain?.id === blessnetSepolia.id) {
      setActiveChainMode(ChainMode.Sepolia);
    } else if (chain?.id === blessnetMainnet.id) {
      setActiveChainMode(ChainMode.Mainnet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, chain?.id]);
};

export default useSwitchToBless;
